// library/update — the Library's pure `update` (module contract) and its
// `inbound`. Model × Msg → Step: the next model, effects as data, and the
// Signals the root routes. The query text and the opened row belong to the
// frame, so the intents that change them are `Signal.FrameRequested`; the
// frame's new state comes back as `shown`, and only then does the module
// start the reads it implies. A late answer (another free text, another item)
// is ignored: a no-op step, not a refusal.

import { exhaustSwitchCase, match, Remote, Result } from "@bilda/utils";
import type { FrameTransition } from "../../app/frame.js";
import {
    askedWords,
    sameWords,
} from "../../components/Search/contentSearch.js";
import type { SearchResultKind } from "../../components/Search/searchModel.js";
import type { Inbound, Step, Update } from "../module.js";
import type { Signal } from "../signal.js";
import type { LibraryEffect } from "./effect.js";
import {
    type Detail,
    type LibraryModel,
    type LoadedKind,
    listPlaceOf,
    type Opened,
} from "./model.js";
import type { LibraryMsg } from "./msg.js";

export type LibraryRefusal = { readonly type: "no_item_open" };

export type LibraryStep = Step<LibraryModel, LibraryEffect>;
type Outcome = Result.Result<LibraryStep, LibraryRefusal>;

const step = (
    model: LibraryModel,
    effects: readonly LibraryEffect[] = [],
    signals: readonly Signal[] = [],
): Outcome => Result.ok({ model, effects, signals });

const frame = (model: LibraryModel, transition: FrameTransition): Outcome =>
    step(model, [], [{ _tag: "Signal.FrameRequested", transition }]);

const loadedKindOf = (kind: SearchResultKind): LoadedKind | undefined => {
    switch (kind) {
        case "source":
        case "package":
        case "file":
            return kind;
        case "material":
        case "conversation":
            return undefined;
    }
};

// Read the opened item (again): keep what it showed while the read runs.
const reload = (model: LibraryModel): Outcome =>
    model.detail._tag === "Detail.Open"
        ? step(
              {
                  ...model,
                  detail: {
                      ...model.detail,
                      load: Remote.startLoad(model.detail.load),
                  },
              },
              [
                  {
                      type: "load_detail",
                      id: model.detail.id,
                      kind: model.detail.kind,
                  },
              ],
          )
        : Result.err({ type: "no_item_open" });

// The query the frame shows: new content words start their content search.
const showQuery = (
    model: LibraryModel,
    query: string,
    contentWords: readonly string[],
): {
    readonly model: LibraryModel;
    readonly effects: readonly LibraryEffect[];
} => {
    const words = askedWords(contentWords);
    if (sameWords(words, model.content.words))
        return { model: { ...model, query }, effects: [] };
    const asked = words.length > 0;
    return {
        model: {
            ...model,
            query,
            content: {
                words,
                hits: asked
                    ? Remote.startLoad(model.content.hits)
                    : Remote.notAsked,
            },
        },
        effects: asked ? [{ type: "search_content", words }] : [],
    };
};

// The row the frame shows opened: a new one is marked and its content read.
const showOpened = (
    model: LibraryModel,
    opened: Opened | null,
): {
    readonly model: LibraryModel;
    readonly effects: readonly LibraryEffect[];
} => {
    const id = opened?.id ?? null;
    if (id === model.openedId) return { model, effects: [] };
    const kind = opened === null ? undefined : loadedKindOf(opened.kind);
    const detail: Detail =
        opened === null || kind === undefined
            ? { _tag: "Detail.Idle" }
            : {
                  _tag: "Detail.Open",
                  id: opened.id,
                  kind,
                  load: Remote.loading,
              };
    return {
        model: { ...model, openedId: id, marked: id ?? model.marked, detail },
        effects:
            detail._tag === "Detail.Open"
                ? [{ type: "load_detail", id: detail.id, kind: detail.kind }]
                : [],
    };
};

export const updateLibrary: Update<
    LibraryModel,
    LibraryMsg,
    LibraryEffect,
    LibraryRefusal
> = (model, msg) => {
    switch (msg.type) {
        case "shown": {
            const q = showQuery(model, msg.query, msg.contentWords);
            const o = showOpened(q.model, msg.opened);
            return step(o.model, [...q.effects, ...o.effects]);
        }
        case "query_edited":
            return frame(model, {
                type: "set-library-query",
                query: msg.query,
            });
        case "row_marked":
            return step({ ...model, marked: msg.id });
        case "row_entered":
            return frame(
                { ...model, marked: msg.id },
                {
                    type: "select-library-row",
                    selectedId: msg.id,
                    ...(msg.target !== undefined ? { target: msg.target } : {}),
                },
            );
        case "stepped":
            return frame(
                { ...model, marked: msg.id },
                { type: "select-library-row", selectedId: msg.id },
            );
        case "list_returned":
            return model.openedId === null
                ? Result.err({ type: "no_item_open" })
                : frame(model, { type: "select-library-row" });
        case "detail_retried":
            return reload(model);
        case "more_shown": {
            const place = listPlaceOf(model, msg.key);
            return step({
                ...model,
                list: { ...place, pages: place.pages + 1 },
            });
        }
        case "list_left":
            return step({
                ...model,
                list: {
                    ...listPlaceOf(model, msg.key),
                    scrollTop: Number.isFinite(msg.scrollTop)
                        ? Math.max(0, msg.scrollTop)
                        : 0,
                },
            });
        case "search_summoned":
            return step({ ...model, summons: model.summons + 1 });
        case "materials_sorted":
            return step({
                ...model,
                sorts: { ...model.sorts, materials: msg.sort },
            });
        case "sources_sorted":
            return step({
                ...model,
                sorts: { ...model.sorts, sources: msg.sort },
            });
        case "content_found":
            return sameWords(msg.words, model.content.words)
                ? step({
                      ...model,
                      content: {
                          words: model.content.words,
                          hits: Remote.loaded(msg.answers),
                      },
                  })
                : step(model);
        case "content_failed":
            return sameWords(msg.words, model.content.words)
                ? step({
                      ...model,
                      content: {
                          words: model.content.words,
                          hits: Remote.failed(msg.error),
                      },
                  })
                : step(model);
        case "detail_loaded":
        case "detail_failed": {
            const { detail } = model;
            if (detail._tag !== "Detail.Open" || detail.id !== msg.id)
                return step(model);
            return step({
                ...model,
                detail: {
                    ...detail,
                    load:
                        msg.type === "detail_loaded"
                            ? Remote.loaded(msg.content)
                            : Remote.failed(msg.error),
                },
            });
        }
        case "world_changed": {
            // Resources committed behind the client: the open item and the
            // content hits are read again (the last values stay shown).
            const { words } = model.content;
            const research: readonly LibraryEffect[] =
                words.length === 0 ? [] : [{ type: "search_content", words }];
            const next: LibraryModel =
                words.length === 0
                    ? model
                    : {
                          ...model,
                          content: {
                              ...model.content,
                              hits: Remote.startLoad(model.content.hits),
                          },
                      };
            const item = reload(next);
            return Result.isOk(item)
                ? step(item.value.model, [...research, ...item.value.effects])
                : step(next, research);
        }
        default:
            return exhaustSwitchCase(msg);
    }
};

export const inboundLibrary: Inbound<LibraryMsg> = match<
    Signal,
    LibraryMsg | undefined
>({
    "Signal.NoticeRaised": () => undefined,
    "Signal.FrameRequested": () => undefined,
    "Signal.WorldChanged": () => ({ type: "world_changed" }),
    "Signal.ConversationStarted": () => undefined,
    "Signal.PendingGroundingFlushed": () => undefined,
    "Signal.ProposalLanded": () => undefined,
    "Signal.StopRequested": () => undefined,
});
